import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeftOutlined,
    CheckCircleFilled,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    FormOutlined,
} from "@ant-design/icons";
import { Button, Empty, Input, Modal, Popconfirm, Result, Spin, Typography, message } from "antd";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import FeedbackPrompt from "../../components/feedback/FeedbackPrompt";
import ExpiredPanel from "../../components/delivery/ExpiredPanel";
import ErrorPanel from "../../components/delivery/ErrorPanel";
import SelectionPhotoPreview from "../../components/selection/SelectionPhotoPreview";
import { useSelectionPayload } from "../../hooks/useSelectionPayload";
import { submitSelection } from "../../utils/selectionApi";
import { downloadDeliveryFile } from "../../utils/deliveryDownload";
import { isExpired } from "../../utils/selectionToken";
import { getAlbumName } from "../../utils/deliveryToken";
import { getFolderDisplayName } from "../../utils/deliveryPath";
import {
    clearPicks,
    getPicks,
    getSelectorName,
    savePicks,
    setSubmittedInfo,
} from "../../utils/selectionStore";
import { useTranslation } from "../../i18n";
import "./selection.css";

const { Title, Text, Paragraph } = Typography;

function SelectionCart() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { status, payload, reload } = useSelectionPayload(token);
    const [picks, setPicks] = useState([]);
    const [selectorName, setSelectorName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);
    const [previewPick, setPreviewPick] = useState(null);
    const [noteEditing, setNoteEditing] = useState(null);
    const [noteDraft, setNoteDraft] = useState("");

    useEffect(() => {
        setPicks(getPicks(token));
        setSelectorName(getSelectorName(token));
    }, [token]);

    useEffect(() => {
        document.title = `${t("selection.cart.title")} · HEI CREATIVE`;
    }, [t]);

    const warnSaveFailed = useCallback(() => {
        message.warning({
            content: t("selection.gallery.saveFailed"),
            key: "selection-save-failed",
            duration: 5,
        });
    }, [t]);

    const removePick = useCallback(
        (id) => {
            setPicks((prev) => {
                const removedIndex = prev.findIndex((p) => p.id === id);
                const next = prev.filter((p) => p.id !== id);
                if (!savePicks(token, next)) {
                    warnSaveFailed();
                }
                // 若正在预览被移除的这张，则在预览中切换到相邻照片，全部移除则关闭预览。
                setPreviewPick((current) => {
                    if (!current || current.id !== id) {
                        return current;
                    }
                    if (next.length === 0) {
                        return null;
                    }
                    return next[Math.min(removedIndex, next.length - 1)];
                });
                return next;
            });
        },
        [token, warnSaveFailed]
    );

    const updateNote = useCallback(
        (id, note) => {
            setPicks((prev) => {
                const next = prev.map((p) => (p.id === id ? { ...p, note } : p));
                if (!savePicks(token, next)) {
                    warnSaveFailed();
                }
                return next;
            });
        },
        [token, warnSaveFailed]
    );

    const openNoteEditor = useCallback((pick) => {
        setNoteEditing(pick);
        setNoteDraft(pick.note || "");
    }, []);

    const closeNoteEditor = useCallback(() => {
        setNoteEditing(null);
        setNoteDraft("");
    }, []);

    const handleSaveNote = useCallback(() => {
        if (!noteEditing) {
            return;
        }
        updateNote(noteEditing.id, noteDraft.trim());
        message.success(t("selection.cart.noteSaved"));
        closeNoteEditor();
    }, [closeNoteEditor, noteDraft, noteEditing, t, updateNote]);

    const handlePreviewDownload = useCallback(
        async (pick) => {
            const hide = message.loading(`${t("selection.gallery.downloading")}…`, 0);
            try {
                await downloadDeliveryFile(pick);
            } catch (error) {
                message.error(error?.message || t("selection.gallery.downloadFailed"));
            } finally {
                hide();
            }
        },
        [t]
    );

    const handleBack = useCallback(() => {
        navigate(`/s/${token}`);
    }, [navigate, token]);

    const handleSubmit = useCallback(async () => {
        if (picks.length === 0 || submitting) {
            return;
        }
        if (!selectorName) {
            message.warning(t("selection.cart.needName"));
            navigate(`/s/${token}`);
            return;
        }
        setSubmitting(true);
        try {
            const result = await submitSelection({
                token,
                selectorName,
                picks: picks.map((p) => ({
                    path: p.path,
                    name: p.name,
                    ...(p.note ? { note: p.note } : {}),
                })),
            });
            const info = {
                id: result?.id || "",
                selectorName,
                photoCount: picks.length,
                submittedAt: Date.now(),
            };
            setSubmittedInfo(token, info);
            clearPicks(token);
            setSubmitted(info);
            message.success(t("selection.cart.submitDone"));
        } catch (error) {
            if (error?.code === "EXPIRED") {
                message.error(t("selection.cart.expired"));
            } else {
                message.error(error?.message || t("selection.cart.submitFailed"));
            }
        } finally {
            setSubmitting(false);
        }
    }, [navigate, picks, selectorName, submitting, t, token]);

    const albumExpired = useMemo(
        () => (payload ? isExpired(payload.e) : false),
        [payload]
    );

    const albumDisplayName = useMemo(() => {
        if (!payload) {
            return "";
        }
        return (
            getAlbumName(payload, getFolderDisplayName(payload.i)) ||
            t("selection.gallery.defaultAlbumName")
        );
    }, [payload, t]);

    if (status === "loading") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <div className="delivery-browser delivery-browser--centered">
                        <Spin size="large" />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (status === "expired" || albumExpired) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ExpiredPanel archiveMarkdown={payload?.archiveMarkdown} />
                </div>
                <Footer />
            </>
        );
    }

    if (status === "error") {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <div className="delivery-browser delivery-browser--centered">
                        <p className="delivery-album-error__text">
                            {t("selection.gallery.albumLoadFailed")}
                        </p>
                        <div className="delivery-album-error__actions">
                            <Button type="primary" onClick={() => navigate("/contact")}>
                                {t("selection.gallery.contactPhotographer")}
                            </Button>
                            <Button onClick={reload}>{t("selection.gallery.retry")}</Button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (status === "invalid" || !payload) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <ErrorPanel />
                </div>
                <Footer />
            </>
        );
    }

    if (submitted) {
        return (
            <>
                <PageTitle>
                    <Navigation />
                </PageTitle>
                <div className="delivery-page">
                    <div className="delivery-card selection-cart__result">
                        <Result
                            icon={<CheckCircleFilled style={{ color: "var(--color-text)" }} />}
                            title={t("selection.cart.successTitle")}
                            subTitle={t("selection.cart.successSubtitle", {
                                name: submitted.selectorName,
                                count: submitted.photoCount,
                            })}
                            extra={
                                <Button type="primary" onClick={handleBack}>
                                    {t("selection.cart.backToGallery")}
                                </Button>
                            }
                        />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <div className="delivery-page selection-cart">
                <div className="selection-cart__header">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={handleBack}
                        className="selection-cart__back"
                    >
                        {t("selection.cart.continue")}
                    </Button>
                    <div className="selection-cart__heading">
                        <Title level={3} style={{ margin: 0 }}>
                            {t("selection.cart.title")}
                        </Title>
                        <Text type="secondary">
                            {t("selection.cart.summary", {
                                name: selectorName || t("selection.cart.anonymous"),
                                count: picks.length,
                            })}
                        </Text>
                    </div>
                </div>

                {picks.length === 0 ? (
                    <div className="delivery-browser--centered">
                        <Empty description={t("selection.cart.empty")} />
                        <Button type="primary" onClick={handleBack}>
                            {t("selection.cart.goSelect")}
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="selection-cart__grid">
                            {picks.map((pick) => (
                                <div key={pick.id} className="selection-cart__item">
                                    {pick.thumbnailUrl ? (
                                        <button
                                            type="button"
                                            className="selection-cart__thumb-btn"
                                            aria-label={pick.name}
                                            onClick={() => setPreviewPick(pick)}
                                        >
                                            <img
                                                src={pick.thumbnailUrl}
                                                alt={pick.name}
                                                className="selection-cart__thumb"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </button>
                                    ) : (
                                        <div className="selection-cart__thumb selection-cart__thumb--empty" />
                                    )}
                                    <button
                                        type="button"
                                        className="selection-cart__remove"
                                        aria-label={t("selection.cart.remove")}
                                        onClick={() => removePick(pick.id)}
                                    >
                                        <CloseOutlined />
                                    </button>
                                    <span className="selection-cart__name">{pick.name}</span>
                                    {pick.note ? (
                                        <p className="selection-cart__note-text" title={pick.note}>
                                            {pick.note}
                                        </p>
                                    ) : null}
                                    <Button
                                        type={pick.note ? "default" : "dashed"}
                                        size="small"
                                        icon={pick.note ? <EditOutlined /> : <FormOutlined />}
                                        className="selection-cart__note-btn"
                                        onClick={() => openNoteEditor(pick)}
                                    >
                                        {pick.note
                                            ? t("selection.cart.note")
                                            : t("selection.cart.noteAdd")}
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <div className="selection-cart__bar">
                            <Text className="selection-cart__bar-count">
                                {t("selection.gallery.selectedCount", { count: picks.length })}
                            </Text>
                            <div className="selection-cart__bar-actions">
                                <Popconfirm
                                    title={t("selection.cart.clearConfirm")}
                                    okText={t("selection.cart.clearOk")}
                                    cancelText={t("selection.cart.clearCancel")}
                                    onConfirm={() => {
                                        clearPicks(token);
                                        setPicks([]);
                                    }}
                                >
                                    <Button icon={<DeleteOutlined />}>
                                        {t("selection.cart.clear")}
                                    </Button>
                                </Popconfirm>
                                <Button
                                    type="primary"
                                    loading={submitting}
                                    onClick={handleSubmit}
                                >
                                    {t("selection.cart.submit")}
                                </Button>
                            </div>
                        </div>
                        <Paragraph type="secondary" className="selection-cart__hint">
                            {t("selection.cart.submitHint")}
                        </Paragraph>
                    </>
                )}
            </div>
            <SelectionPhotoPreview
                file={previewPick}
                open={Boolean(previewPick)}
                picked
                note={previewPick?.note || ""}
                onClose={() => setPreviewPick(null)}
                onDownload={handlePreviewDownload}
                onTogglePick={(pick) => removePick(pick.id)}
                onNoteChange={(pick, value) => {
                    updateNote(pick.id, value);
                    setPreviewPick((current) =>
                        current && current.id === pick.id ? { ...current, note: value } : current
                    );
                }}
                files={picks}
                onNavigate={setPreviewPick}
            />
            <Modal
                open={Boolean(noteEditing)}
                title={t("selection.cart.noteEditTitle")}
                onCancel={closeNoteEditor}
                okText={t("selection.cart.noteSave")}
                cancelText={t("selection.cart.noteCancel")}
                onOk={handleSaveNote}
                destroyOnClose
            >
                {noteEditing && (
                    <>
                        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                            {noteEditing.name}
                        </Text>
                        <Input.TextArea
                            value={noteDraft}
                            onChange={(event) => setNoteDraft(event.target.value)}
                            placeholder={t("selection.cart.notePlaceholder")}
                            autoSize={{ minRows: 3, maxRows: 8 }}
                            maxLength={2000}
                            autoFocus
                        />
                    </>
                )}
            </Modal>
            {payload ? (
                <FeedbackPrompt
                    className="feedback-prompt--cart"
                    sourceType="selection"
                    linkId={token}
                    albumPath={payload.i}
                    albumName={albumDisplayName}
                />
            ) : null}
            <Footer />
        </>
    );
}

export default SelectionCart;
